// The "Activated cities" checkboxes' state transitions, extracted from the
// fieldset so the only arithmetic in the feature can be tested without
// rendering anything — same split, and the same reason, as
// partner-products-state.ts.
//
// Why these four functions and not inline `.filter()` calls: a per-country
// "All in X" box is the one control here that moves several values at once,
// and it has three states the eye cannot distinguish from the click (none /
// some / all of that country's cities on). Getting "some" wrong — clearing a
// country when the admin meant to complete it — silently deactivates cities
// they had already ticked, and no error ever says so.
//
// The API (PUT /admin/partners/:id/locations → setPartnerLocations) stays the
// authority: it is a replace-set and it refuses any id that is not a city.
// This is the guard rail in front of it, not a second implementation of it.

/** A published city the picker may offer. `countryName` is whatever the list
 *  carried — the admin locations list joins `en` names, and a row with none
 *  falls back to its code at render time. */
export interface ActivatableCity {
  id: number;
  name: string;
  countryCode: string | null;
  countryName?: string | null;
}

const fold = (code: string | null | undefined): string => (code ?? '').toLowerCase();

/** On ⇄ off for one city. Deduplicates on the way in: an id that somehow
 *  arrived twice must not need two clicks to come off. */
export function toggleCity(selected: number[], cityId: number): number[] {
  if (selected.includes(cityId)) return selected.filter((id) => id !== cityId);
  return [...selected, cityId];
}

/** Every offered city sitting in that country, in list order. */
export function countryCityIds(cities: ActivatableCity[], countryCode: string): number[] {
  const code = fold(countryCode);
  return cities.filter((c) => fold(c.countryCode) === code).map((c) => c.id);
}

/**
 * True only when the country HAS cities here and all of them are on. An empty
 * group is deliberately false rather than vacuously true — a ticked box over
 * nothing reads as "this country is activated", which it is not.
 */
export function isCountryFullySelected(
  cities: ActivatableCity[],
  selected: number[],
  countryCode: string,
): boolean {
  const ids = countryCityIds(cities, countryCode);
  if (ids.length === 0) return false;
  const on = new Set(selected);
  return ids.every((id) => on.has(id));
}

/**
 * "All in <country>": selects every city of it unless every one is ALREADY
 * on, in which case it clears exactly those. Partially-selected therefore
 * completes rather than clears — ticking a box is how an admin says "all of
 * these", and treating a half-full group as "clear it" would take away cities
 * they had chosen one by one.
 *
 * Cities of other countries are never touched, whichever way it goes.
 */
export function toggleCountry(
  cities: ActivatableCity[],
  selected: number[],
  countryCode: string,
): number[] {
  const ids = countryCityIds(cities, countryCode);
  if (ids.length === 0) return selected;
  if (isCountryFullySelected(cities, selected, countryCode)) {
    const drop = new Set(ids);
    return selected.filter((id) => !drop.has(id));
  }
  const on = new Set(selected);
  return [...selected, ...ids.filter((id) => !on.has(id))];
}

/**
 * "N cities in M countries" for the summary line. Counts DISTINCT countries,
 * and counts a selected id the picker no longer knows about (a city
 * unpublished since the page loaded) as a city with no country rather than
 * dropping it — the partner is still activated there, and hiding that from
 * the count is how the number stops matching what Save will send.
 */
export function activationCounts(
  cities: ActivatableCity[],
  selected: number[],
): { cities: number; countries: number } {
  const byId = new Map(cities.map((c) => [c.id, c]));
  const ids = [...new Set(selected)];
  const countries = new Set<string>();
  for (const id of ids) {
    const code = fold(byId.get(id)?.countryCode);
    if (code) countries.add(code);
  }
  return { cities: ids.length, countries: countries.size };
}
